import { FC } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import Page from 'modules/_shared/components/Pages/Page';
import CustomLoading from 'modules/_shared/components/CustomLoading';

import { useIncentiveOfferDetail } from './context/IncentiveOfferDetailContext';
import IncentiveOfferDetailHeader from './components/IncentiveOfferDetailHeader';
import IncentiveOfferStatusBanner from './components/IncentiveOfferStatusBanner';
import IncentiveOfferDetailSummaryCard from './components/IncentiveOfferDetailSummaryCard';
import IncentiveOfferPriceContextCard from './components/IncentiveOfferPriceContextCard';
import IncentiveOfferAcceptanceProgress from './components/IncentiveOfferAcceptanceProgress';
import IncentiveOfferOperationsTable from './components/IncentiveOfferOperationsTable';
import IncentiveOfferTimeline from './components/IncentiveOfferTimeline';
import IncentiveOfferPublisherInfoCard from './components/IncentiveOfferSellerInfoCard';
import IncentiveOfferActionCard from './components/IncentiveOfferActionCard';
import IncentiveOfferSellerLimitsBanner from './components/IncentiveOfferSellerLimitsBanner';

const IncentiveOfferDetail: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useIncentiveOfferDetail();

  if (data.isLoading) {
    return <CustomLoading />;
  }

  if (!data.offer) {
    return (
      <Page id="trading-offer-detail-page">
        <div className="nk-block text-center py-5">
          <em className="icon ni ni-alert-circle fs-1 text-warning mb-3 d-block" />
          <h5>{t('OfferNotFound')}</h5>
          <p className="text-soft">{t('OfferNotFoundDescription')}</p>
          <button
            type="button"
            className="btn btn-primary mt-2"
            onClick={actions.handleGoBack}
          >
            {t('Back')}
          </button>
        </div>
      </Page>
    );
  }

  return (
    <Page id="trading-offer-detail-page">
      <IncentiveOfferDetailHeader
        offer={data.offer}
        offerReference={data.offerReference}
        offerStatus={data.offerStatus}
        societyId={data.societyId}
        societyName={data.societyName}
        isAdmin={data.isAdmin}
        userBeneficiaryIds={data.userBeneficiaryIds}
        beneficiaryQueryParam={data.beneficiaryQueryParam}
      />

      <div className="nk-block">
        <IncentiveOfferStatusBanner
          offer={data.offer}
          offerStatus={data.offerStatus}
        />

        <IncentiveOfferSellerLimitsBanner
          offer={data.offer}
          totalUnits={data.totalAvailableUnits}
          sellableUnits={data.sellerOfferCap?.maxUnitsSellerCanOffer}
        />

        <div className="row g-gs">
          <div className="col-lg-7">
            <IncentiveOfferDetailSummaryCard
              offer={data.offer}
              offerReference={data.offerReference}
              availableShares={data.availableShares}
              daysRemaining={data.daysRemaining}
            />

            <IncentiveOfferPriceContextCard priceContext={data.priceContext} />

            <IncentiveOfferAcceptanceProgress
              offer={data.offer}
              acceptanceHistory={data.acceptanceHistory}
            />

            <IncentiveOfferOperationsTable
              operations={data.incentiveOfferOperations}
              offerType={data.offer.type}
              onGoToOperation={actions.handleGoToOperation}
            />

            <IncentiveOfferTimeline steps={data.timelineSteps} />

            {data.sellerInfo && (
              <IncentiveOfferPublisherInfoCard
                sellerInfo={data.sellerInfo}
                offerType={data.offer.type}
              />
            )}
          </div>

          <div className="col-lg-5">
            <IncentiveOfferActionCard
              offer={data.offer}
              availableShares={data.availableShares}
              sellerOfferCap={data.sellerOfferCap}
              sellerPlans={data.sellerPlans}
              purchaseQuantity={data.purchaseQuantity}
              isExpired={data.isExpired}
              isOwnOffer={data.isOwnOffer}
              hasPartnerBuyer={data.hasPartnerBuyer}
              onQuantityChange={actions.handlePurchaseQuantityChange}
              onBuyShares={actions.handleBuyShares}
              onViewOtherOffers={actions.handleViewOtherOffers}
            />
          </div>
        </div>
      </div>

      {data.isLoading && <CustomLoading />}
    </Page>
  );
};

export default IncentiveOfferDetail;
